import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type ApiErr } from '../lib/api';
import { Banner, Block, Cell, Grid, Kpis, Log, Seg, Table, type Tone } from '../ui/Block';
import { Head, useAdmin } from '../ui/Shell';
import { downloadText } from './Analytics';
import { SimEngine, type Command, type Decision, type Scenario, type SimConfig, type Snapshot } from './simulator/engine';
import { Flow, FlowLegend } from './simulator/Flow';
import { LineChart } from './simulator/LineChart';
import { Money } from './simulator/Money';
import { arrival, fetchJobs, scenarioOf } from './simulator/realData';
import { Settings } from './simulator/Settings';
import { SITUATION, Scenarios, situationCommands, situationConfig, type Situation } from './simulator/Scenarios';

type Mode = 'mock' | 'real' | 'file';
const SPEEDS = ['1', '10', '30', '60'] as const;
type Speed = (typeof SPEEDS)[number];
const MAX_STEP_S = 6 * 3600;
const SNAP_MS = 100;
const POLL_MS = 15_000;

const sourceOf = (envKey: string) => (envKey === 'production' ? 'prd' : envKey);
const pad = (n: number) => String(n).padStart(2, '0');
const localInput = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const dur = (s: number) => (s < 120 ? `${Math.round(s)} s` : s < 7200 ? `${Math.round(s / 60)} min` : `${(s / 3600).toFixed(1)} h`);

export function Simulator() {
  const ctx = useAdmin();
  const { env, mobile } = ctx;
  const eng = useRef<SimEngine | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [snap, setSnap] = useState<Snapshot | null>(null);
  const [mode, setMode] = useState<Mode>('mock');
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState<Speed>('1');
  const [cfg, setCfg] = useState<SimConfig | null>(null);
  const [applied, setApplied] = useState<SimConfig | null>(null);
  const [logKinds, setLogKinds] = useState({ pods: true, moves: true, places: false });
  const [sit, setSit] = useState<Situation>(SITUATION);
  const now = new Date();
  const [from, setFrom] = useState(localInput(new Date(now.getTime() - 24 * 3600_000)));
  const [to, setTo] = useState(localInput(now));
  const [live, setLive] = useState(false);
  const [realBusy, setRealBusy] = useState(false);
  const [realErr, setRealErr] = useState<string | null>(null);
  const [realNote, setRealNote] = useState<string | null>(null);
  const liveRef = useRef<{ t0: number; seen: Map<string, boolean>; lastPoll: number } | null>(null);
  const playRef = useRef({ playing, speed });
  playRef.current = { playing, speed };

  const refresh = useCallback(() => { if (eng.current) setSnap(eng.current.snapshot()); }, []);

  useEffect(() => {
    let off = false;
    SimEngine.load().then((e) => {
      if (off) return;
      eng.current = e;
      const d = e.defaults();
      e.init(d);
      setCfg(d);
      setApplied(d);
      setSnap(e.snapshot());
    }).catch((e: Error) => setLoadErr(e.message));
    return () => { off = true; };
  }, []);

  useEffect(() => {
    let raf = 0;
    let last = performance.now();
    let acc = 0;
    let lastSnap = 0;
    const frame = (t: number) => {
      const e = eng.current;
      const dt = Math.min((t - last) / 1000, 1);
      last = t;
      if (e) {
        const { playing: on, speed: sp } = playRef.current;
        const lv = liveRef.current;
        let ran = false;
        if (lv) {
          const behind = Date.now() / 1000 - lv.t0 - e.now();
          if (behind >= 1) { e.run(Math.min(behind, MAX_STEP_S)); ran = true; }
        } else if (on) {
          acc += dt * Number(sp);
          const whole = Math.floor(Math.min(acc, MAX_STEP_S));
          if (whole >= 1) { e.run(whole); acc -= whole; ran = true; }
        }
        if (ran && t - lastSnap >= SNAP_MS) { lastSnap = t; setSnap(e.snapshot()); }
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, []);

  const send = (c: Command) => {
    try { eng.current?.command(c); refresh(); } catch (e) { ctx.toast('Command failed', (e as Error).message, { logged: false }); }
  };

  const stopLive = () => { liveRef.current = null; };

  const startMock = (c: SimConfig) => {
    stopLive();
    const e = eng.current;
    if (!e) return;
    const m = { ...c, traffic: { ...c.traffic, generate: true } };
    e.init(m);
    setCfg(m);
    setApplied(m);
    setPlaying(true);
    refresh();
  };

  const startSituation = () => {
    if (!cfg) return;
    const c = situationConfig(cfg, sit);
    startMock(c);
    for (const x of situationCommands(sit)) send(x);
    ctx.toast('Situation started', `${sit.live} live × ${sit.voices} voices${sit.reading ? ` · a reading of ${sit.reading} lines` : ''}`, { logged: false });
  };

  const addSituation = () => {
    if (!cfg || !applied) return;
    const c = situationConfig(cfg, sit);
    const next = { ...c, traffic: { ...c.traffic, arrivalsPerH: applied.traffic.arrivalsPerH, generate: applied.traffic.generate } };
    send({ cmd: 'balancer', config: next.balancer });
    send({ cmd: 'traffic', traffic: next.traffic });
    send({ cmd: 'world', world: next.world });
    for (const x of situationCommands(sit)) send(x);
    setCfg(next);
    setApplied(next);
  };

  const applyNow = () => {
    if (!cfg || !applied) return;
    if (JSON.stringify(cfg.balancer) !== JSON.stringify(applied.balancer)) send({ cmd: 'balancer', config: cfg.balancer });
    if (JSON.stringify(cfg.traffic) !== JSON.stringify(applied.traffic)) send({ cmd: 'traffic', traffic: { ...cfg.traffic, generate: applied.traffic.generate } });
    if (JSON.stringify(cfg.world) !== JSON.stringify(applied.world)) send({ cmd: 'world', world: cfg.world });
    if (JSON.stringify(cfg.pricing) !== JSON.stringify(applied.pricing)) send({ cmd: 'pricing', pricing: cfg.pricing });
    setApplied(cfg);
    ctx.toast('Settings applied', 'Recorded in the run: an export replays them at this moment', { logged: false });
  };

  const loadReal = async () => {
    const e = eng.current;
    if (!e || !cfg) return;
    setRealBusy(true);
    setRealErr(null);
    stopLive();
    try {
      const f = new Date(from);
      const t = live ? new Date() : new Date(to);
      if (!(f < t)) throw new Error('The start must be before the end.');
      const jobs = await fetchJobs(env, f, t);
      const sc = scenarioOf(jobs, f, t, cfg, sourceOf(env.key));
      e.replay(sc);
      setApplied(sc.config);
      if (live) {
        e.run(sc.endT);
        const seen = new Map(jobs.map((j) => [j.id, !!j.endedAt] as [string, boolean]));
        liveRef.current = { t0: sc.t0Unix!, seen, lastPoll: Date.now() };
        setRealNote(`${jobs.length} recordings since ${f.toLocaleString()}; following ${env.label} live`);
      } else {
        setRealNote(`${jobs.length} recordings, ${f.toLocaleString()} → ${t.toLocaleString()}`);
        setPlaying(true);
      }
      refresh();
    } catch (err) {
      const m = (err as ApiErr).message ?? String(err);
      setRealErr(/unknown panel/.test(m) ? `${env.label}'s API has no engine-jobs panel yet: it ships with the release that carries this simulator.` : m);
    } finally {
      setRealBusy(false);
    }
  };

  useEffect(() => {
    const id = window.setInterval(async () => {
      const lv = liveRef.current;
      const e = eng.current;
      if (!lv || !e || !cfg) return;
      try {
        const jobs = await fetchJobs(env, new Date(lv.lastPoll - 120_000), new Date());
        lv.lastPoll = Date.now();
        for (const j of jobs) {
          const was = lv.seen.get(j.id);
          if (was === undefined) {
            e.command(arrival(j, lv.t0, cfg.traffic.fileSpeed));
            lv.seen.set(j.id, !!j.endedAt);
          } else if (!was && j.endedAt) {
            e.command({ cmd: 'end', ext: j.id });
            lv.seen.set(j.id, true);
          }
        }
        refresh();
      } catch (err) { setRealErr(`Live poll: ${(err as Error).message}`); }
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [env, cfg, refresh]);

  const exportRun = () => {
    const e = eng.current;
    if (!e || !snap) return;
    const label = `${snap.source} at ${clock(snap)}`;
    const text = e.exportText(label);
    const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
    downloadText(`simulation-${snap.source.replace(/\s+/g, '-')}-${stamp}.json`, text, 'application/json');
    ctx.toast('Simulation exported', `${snap.recorded + snap.queued} commands; open it under Replay a file to play it again`, { logged: false });
  };

  const loadFile = async (file: File) => {
    const e = eng.current;
    if (!e) return;
    stopLive();
    try {
      const sc = JSON.parse(await file.text()) as Scenario;
      if (!sc || !Array.isArray(sc.timeline) || !sc.config) throw new Error('Not a simulator export.');
      e.replay(sc);
      setCfg(sc.config);
      setApplied(sc.config);
      setRealNote(`${sc.label || file.name}: ${sc.timeline.length} commands, ${dur(sc.endT)} long`);
      setPlaying(true);
      refresh();
    } catch (err) { ctx.toast('Could not replay the file', (err as Error).message, { logged: false }); }
  };

  const series = snap?.series ?? [];
  const xs = series.map((s) => s.t);
  const fmtX = useCallback((t: number) => (snap ? clockAt(snap, t) : ''), [snap?.t0Unix]); // eslint-disable-line react-hooks/exhaustive-deps
  const log = useMemo(() => (snap?.log ?? []).filter((d) => kindOf(d) === 'pods' ? logKinds.pods : kindOf(d) === 'moves' ? logKinds.moves : logKinds.places), [snap?.log, logKinds]);

  if (loadErr) return <><Head title="GPU pool simulator" /><Block><Banner icon="warn" fg="var(--conf-low)" text="The simulator could not load" sub={`${loadErr}. Build it with make balancer-wasm.`} /></Block></>;
  if (!snap || !cfg) return <><Head title="GPU pool simulator" /><Block loading /></>;

  const m = snap.metrics;
  const v = snap.voice;
  const speechUp = snap.pods.filter((p) => p.speechOnly && (p.state === 'ready' || p.state === 'draining'));
  const onFly = snap.sessions.filter((s) => s.place.at === 'fly').length;
  const onGpu = snap.sessions.filter((s) => s.place.at === 'pod').length;
  const waiting = snap.sessions.filter((s) => s.place.at === 'waiting').length;
  const podsUp = snap.pods.filter((p) => p.state === 'ready' || p.state === 'draining').length;
  const podsBooting = snap.pods.filter((p) => p.state === 'creating' || p.state === 'booting').length;
  const d = snap.demand;
  const following = !!liveRef.current;
  const dirty = !!applied && JSON.stringify(cfg) !== JSON.stringify(applied);

  return (
    <>
      <Head title="GPU pool simulator"
        sub="The balancer the API will run, compiled from balancer/, deciding for a simulated Fly and RunPod. Mock traffic, an environment's real recordings, or a replayed export."
        acts={<>
          <button type="button" className="btn" onClick={() => ctx.go('gpu-pool')}>GPU pool</button>
          <button type="button" className="btn" onClick={exportRun} title="Settings and every command so far, to replay exactly">Export run</button>
        </>} />
      <Grid>
        <Cell span={12}>
          <Block>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px 16px', flexWrap: 'wrap', padding: '11px 14px' }}>
              <Seg<Mode> value={mode} onChange={(v) => { setMode(v); if (v === 'mock') startMock(cfg); }} options={[{ value: 'mock', label: 'Mock traffic' }, { value: 'real', label: `Real · ${env.label}` }, { value: 'file', label: 'Replay a file' }]} />
              <Clock snap={snap} />
              <span style={{ flex: 1 }} />
              {following ? (
                <>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--live)', display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--live)' }} />Following live</span>
                  <button type="button" className="btn btn--sm" onClick={() => { stopLive(); setPlaying(false); refresh(); }}>Stop following</button>
                </>
              ) : (
                <>
                  <button type="button" className="btn btn--sm btn--primary" onClick={() => setPlaying(!playing)}>{playing ? 'Pause' : 'Play'}</button>
                  <button type="button" className="btn btn--sm" onClick={() => { eng.current?.run(60); refresh(); }} disabled={playing}>+1 min</button>
                  <Seg<Speed> value={speed} onChange={setSpeed} mono options={SPEEDS.map((s) => ({ value: s, label: `${s}×` }))} />
                </>
              )}
            </div>
            {mode === 'mock' && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '0 14px 12px', alignItems: 'center' }}>
                <span className="tbl__h" style={{ marginRight: 4 }}>RUSH</span>
                <button type="button" className="btn btn--sm" onClick={() => send({ cmd: 'rush', n: 4, lang: 'multilingual', kind: 'live' })}>+4 Multilingual live</button>
                <button type="button" className="btn btn--sm" onClick={() => send({ cmd: 'rush', n: 4, lang: 'english', kind: 'live' })}>+4 English live</button>
                <button type="button" className="btn btn--sm" onClick={() => send({ cmd: 'rush', n: 2, lang: 'multilingual', kind: 'file' })}>+2 files</button>
              </div>
            )}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '0 14px 12px', alignItems: 'center' }}>
              <span className="tbl__h" style={{ marginRight: 4 }}>REMOVE</span>
              <button type="button" className="btn btn--sm" title="The 4 newest end, as if their users stopped them" onClick={() => send({ cmd: 'remove', n: 4, kind: 'live', lang: 'multilingual' })}>−4 Multilingual live</button>
              <button type="button" className="btn btn--sm" onClick={() => send({ cmd: 'remove', n: 4, kind: 'live', lang: 'english' })}>−4 English live</button>
              <button type="button" className="btn btn--sm" onClick={() => send({ cmd: 'remove', n: 2, kind: 'file' })}>−2 files</button>
              <button type="button" className="btn btn--sm btn--danger" disabled={!snap.sessions.length} onClick={() => send({ cmd: 'remove', n: 1_000_000 })}>End all · {snap.sessions.length}</button>
              <span style={{ fontSize: 12, color: 'var(--ink-55)' }}>or click a dot to end that one.</span>
            </div>
            {mode === 'real' && (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', padding: '0 14px 12px', alignItems: 'flex-end' }}>
                <label className="field" style={{ margin: 0 }}><span className="field__label">From</span><input className="field__input" type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
                {!live && <label className="field" style={{ margin: 0 }}><span className="field__label">To</span><input className="field__input" type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} /></label>}
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink-62)', paddingBottom: 8 }}><input type="checkbox" checked={live} onChange={(e) => setLive(e.target.checked)} />Up to now, then follow live</label>
                <button type="button" className="btn btn--primary" disabled={realBusy} onClick={() => void loadReal()}>{realBusy ? 'Loading recordings…' : `Load ${env.label}'s recordings`}</button>
                <span style={{ fontSize: 12, color: 'var(--ink-55)', paddingBottom: 8 }}>Real arrivals and lengths; the pods, boots and failures are simulated. The top bar switches environment.</span>
              </div>
            )}
            {mode === 'file' && (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', padding: '0 14px 12px', alignItems: 'center' }}>
                <input type="file" accept=".json,application/json" onChange={(e) => { const f = e.target.files?.[0]; if (f) void loadFile(f); e.target.value = ''; }} />
                <span style={{ fontSize: 12, color: 'var(--ink-55)' }}>An export from here (mock or real) plays again exactly: same decisions, same numbers.</span>
              </div>
            )}
            {realErr && mode === 'real' && <Banner icon="warn" fg="var(--conf-low)" text="Could not load the recordings" sub={realErr} />}
            {realNote && mode !== 'mock' && <div style={{ padding: '0 14px 12px', fontSize: 12, color: 'var(--ink-62)' }}>{realNote}</div>}
          </Block>
        </Cell>

        <Cell span={12}>
          <Block title="Today's situations" sub="Live streams with spoken translations, readings, RunPod's stock, Vast's boots and refusals, and what a speech machine can make. Start one as a fresh day (GPU first, voices to Vast first) or add it to the running one.">
            <Scenarios s={sit} set={setSit} onStart={startSituation} onAdd={addSituation} />
          </Block>
        </Cell>

        <Cell span={mobile ? 12 : 5}>
          <Block title="Money" sub="Of every dollar customers paid in this run: what Fly machines and GPU pods cost, and what was left as profit.">
            <Money m={m} />
          </Block>
        </Cell>
        <Cell span={mobile ? 12 : 7}>
          <Kpis min={150} items={[
            { label: 'Sessions', v: `${onFly + onGpu + waiting}`, sub: `${onFly} on Fly · ${onGpu} on GPU${waiting ? ` · ${waiting} waiting` : ''}` },
            { label: 'Pods', v: `${podsUp}${podsBooting ? ` + ${podsBooting}` : ''}`, sub: podsBooting ? `${podsBooting} coming up · peak ${m.peakPods}` : `peak ${m.peakPods}` },
            { label: 'Demand', v: d.overflow.toFixed(1), sub: d.blocked ? `no new pod: ${d.blocked}` : d.pressure ? `${d.pressure}: pods start at once` : d.startIn !== null ? `over ${d.startAt.toFixed(1)}: a pod in ${Math.ceil(d.startIn)} s` : `starts at ≥ ${d.startAt.toFixed(1)}`, fg: d.blocked && d.overflow > 0 ? 'var(--conf-low)' : d.pressure ? 'var(--conf-mid)' : undefined },
            { label: 'Moves', v: `${m.moves}`, sub: `${m.rescues} rescues · ${m.movesFailed} failed` },
            { label: 'Wait to first text', v: m.waitN ? dur(m.waitSumS / m.waitN) : '–', sub: `max ${dur(m.waitMaxS)} · gap max ${dur(m.gapMaxS)}` },
            { label: 'Idle, no machine', v: m.queuedSessions ? dur(m.queuedS) : '0', sub: <span title={`Queued for a machine: ${m.queuedSessions} sessions, ${dur(m.queuedS)} in all, the longest ${dur(m.queuedMaxS)}. Held for a pod about to be ready: ${m.podWaitSessions} files, ${dur(m.podWaitS)}. Fly machines booting under a session: ${dur(m.bootingS)}.`}>{m.queuedSessions} queued, max {dur(m.queuedMaxS)} · {m.podWaitSessions} held</span>, fg: m.queuedSessions ? 'var(--conf-mid)' : undefined },
            { label: 'Turned away', v: `${m.rejected + m.dropped}`, sub: `${m.dropped} dropped mid-session`, fg: m.rejected + m.dropped ? 'var(--conf-low)' : undefined },
            { label: 'Files paused', v: `${m.pauses ?? 0}`, sub: <span title={`Files that gave their place to a live session with none: ${m.pauses ?? 0}, ${m.resumes ?? 0} gone on again from their last committed chunk; ${dur(m.pausedS ?? 0)} paused in all, the longest ${dur(m.pausedMaxS ?? 0)}. Live sessions that waited for a pod about to be ready: ${m.liveWaits ?? 0}.`}>{m.resumes ?? 0} resumed · max {dur(m.pausedMaxS ?? 0)} paused</span>, fg: m.pauses ? 'var(--conf-mid)' : undefined },
            { label: 'Voices', v: v && (v.wantedPerMin > 0 || v.readingLeft > 0 || (m.voiceLines ?? 0) > 0) ? `${Math.round(v.lagS)} s behind` : '–', sub: v ? <span title={`Live voice lines wanted ${Math.round(v.wantedPerMin)} a minute, made ${Math.round(v.madePerMin)} last minute; ${Math.round(v.liveWaiting)} live lines waiting, ${Math.round(v.readingLeft)} reading lines left. ${v.speakers} machine(s) speaking${v.borrowed ? ' (a transcription pod lent its spare room)' : ''}, able to make ${Math.round(v.canPerMin)} a minute. Worst lag in the run: ${dur(m.voiceLagMaxS ?? 0)}. Learned per GPU: ${Object.entries(snap.speechRates ?? {}).map(([g, r]) => `${g} ${Math.round(r)}/min`).join(', ') || 'nothing yet'}.`}>{Math.round(v.wantedPerMin)}/min wanted · {Math.round(v.canPerMin)}/min can · {speechUp.length} speech up{v.borrowed ? ' · borrowed' : ''}</span> : undefined, fg: v && v.lagS > 30 ? 'var(--conf-low)' : v && v.lagS > 10 ? 'var(--conf-mid)' : undefined },
            { label: 'Rule violations', v: `${m.violations}`, sub: m.violations ? m.firstViolations[0] : 'checked every step', fg: m.violations ? 'var(--conf-low)' : 'var(--ok)' },
          ]} />
        </Cell>

        <Cell span={12}>
          <Block title="Fleet" sub="Each dot is one transcription. It keeps its machine or slot until the balancer moves it; a moving dot is a handoff." foot={<FlowLegend />}>
            <div style={{ padding: 14 }}>
              <Flow snap={snap} mobile={mobile} onKill={(pod) => send({ cmd: 'kill', pod })} onEnd={(session) => send({ cmd: 'endSession', session })} />
            </div>
          </Block>
        </Cell>

        <Cell span={mobile ? 12 : 4}>
          <Block title="Demand" sub="Weighted sessions with no GPU slot (Multilingual 1, English 0.5), against the start and drain lines.">
            <div style={{ padding: '8px 10px 10px' }}>
              <LineChart xs={xs} fmtX={fmtX} fmtY={(v) => v.toFixed(1)} lines={[
                { label: 'Demand', vals: series.map((s) => s.overflow), color: 'var(--brand)' },
                { label: 'Start line', vals: series.map((s) => (Number.isFinite(s.startAt) ? s.startAt : NaN)), color: 'var(--ink-62)', dash: true },
                { label: 'Drain line', vals: series.map((s) => (Number.isFinite(s.drainAt) ? s.drainAt : NaN)), color: 'var(--ink-40)', dash: true },
              ]} />
            </div>
          </Block>
        </Cell>
        <Cell span={mobile ? 12 : 4}>
          <Block title="Where sessions run" sub="Sessions on Fly machines and in GPU slots, and files waiting for a machine or a pod, each minute.">
            <div style={{ padding: '8px 10px 10px' }}>
              <LineChart xs={xs} fmtX={fmtX} fmtY={(v) => String(Math.round(v))} lines={[
                { label: 'On GPU', vals: series.map((s) => s.onGpu), color: 'var(--brand)' },
                { label: 'On Fly', vals: series.map((s) => s.onFly), color: 'var(--ink-55)' },
                { label: 'Waiting', vals: series.map((s) => s.waiting), color: 'var(--conf-mid)', dash: true },
              ]} />
            </div>
          </Block>
        </Cell>
        <Cell span={mobile ? 12 : 4}>
          <Block title="Cost per hour" sub="What the fleet costs right now, against everything on Fly.">
            <div style={{ padding: '8px 10px 10px' }}>
              <LineChart xs={xs} fmtX={fmtX} fmtY={(v) => `$${v.toFixed(2)}`} lines={[
                { label: 'With GPU', vals: series.map((s) => s.costRate), color: 'var(--brand)' },
                { label: 'All on Fly', vals: series.map((s) => s.baselineRate), color: 'var(--ink-55)', dash: true },
              ]} />
            </div>
          </Block>
        </Cell>

        <Cell span={mobile ? 12 : 4}>
          <Block title="Machines" sub="Fly engines (one a session, and those idling after one), RunPod transcription pods, RunPod speech-only pods and Vast speech machines, each minute.">
            <div style={{ padding: '8px 10px 10px' }}>
              <LineChart xs={xs} fmtX={fmtX} fmtY={(v) => String(Math.round(v))} lines={[
                { label: 'Fly engines', vals: series.map((s) => s.flyMachines ?? NaN), color: 'var(--ink-55)' },
                { label: 'RunPod pods', vals: series.map((s) => s.runpodPods ?? NaN), color: 'var(--brand)' },
                { label: 'RunPod speech', vals: series.map((s) => s.speechPods ?? NaN), color: 'var(--conf-mid)' },
                { label: 'Vast speech', vals: series.map((s) => s.vastMachines ?? NaN), color: 'var(--ok)' },
              ]} />
            </div>
          </Block>
        </Cell>
        <Cell span={mobile ? 12 : 4}>
          <Block title="Voice lines" sub="Live voice lines wanted and made a minute, and the lines waiting for a lane (live and reading).">
            <div style={{ padding: '8px 10px 10px' }}>
              <LineChart xs={xs} fmtX={fmtX} fmtY={(v) => String(Math.round(v))} lines={[
                { label: 'Wanted /min', vals: series.map((s) => s.voiceWantedPerMin ?? NaN), color: 'var(--ink-55)', dash: true },
                { label: 'Made /min', vals: series.map((s) => s.voiceMadePerMin ?? NaN), color: 'var(--brand)' },
                { label: 'Waiting', vals: series.map((s) => (s.voiceWaiting ?? 0) + (s.readingLeft ?? 0)), color: 'var(--conf-mid)' },
              ]} />
            </div>
          </Block>
        </Cell>
        <Cell span={mobile ? 12 : 4}>
          <Block title="Voice lag" sub="How far live voices run behind the words they speak (lines waiting over lines wanted a second), each minute.">
            <div style={{ padding: '8px 10px 10px' }}>
              <LineChart xs={xs} fmtX={fmtX} fmtY={(v) => `${Math.round(v)} s`} lines={[
                { label: 'Lag', vals: series.map((s) => s.voiceLagS ?? NaN), color: 'var(--conf-low)' },
              ]} />
            </div>
          </Block>
        </Cell>

        <Cell span={12}>
          <Block title="By GPU type" sub="The pods of each type RunPod gave in this run: created, never up, boot times, cost and sessions served; creates RunPod refused, by group."
            empty={Object.keys(m.byGpu ?? {}).length || Object.keys(m.refused ?? {}).length ? undefined : 'No pod yet.'}>
            <Table mobile={mobile} minWidth={760}
              cols={[{ label: 'Type', width: 'minmax(140px, 1fr)' }, { label: 'Pods', width: '90px', align: 'r' }, { label: 'Never up', width: '90px', align: 'r' }, { label: 'Container', width: '100px', align: 'r' }, { label: 'Ready', width: '100px', align: 'r' }, { label: 'Up', width: '90px', align: 'r' }, { label: 'Sessions', width: '90px', align: 'r' }, { label: 'Cost', width: '90px', align: 'r' }]}
              rows={[
                ...Object.entries(m.byGpu ?? {}).filter(([k]) => k).map(([k, g]) => ({ key: k, cells: [
                  { t: k }, { t: String(g.created), mono: true }, { t: String(g.failed), mono: true, fg: g.failed ? 'var(--conf-low)' : undefined },
                  { t: g.created - g.failed > 0 ? dur(g.containerS / Math.max(1, g.created - g.failed)) : '–', mono: true },
                  { t: g.ready ? dur(g.readyS / g.ready) : '–', mono: true }, { t: dur(g.upS), mono: true }, { t: String(g.sessions), mono: true }, { t: `$${g.cost.toFixed(2)}`, mono: true },
                ] })),
                ...Object.entries(m.refused ?? {}).map(([k, n]) => ({ key: `refused-${k}`, cells: [{ t: `${k}: refused`, fg: 'var(--conf-mid)' }, { t: String(n), mono: true }, { t: '' }, { t: '' }, { t: '' }, { t: '' }, { t: '' }, { t: '$0.00', mono: true }] })),
              ]} />
          </Block>
        </Cell>

        <Cell span={mobile ? 12 : 7}>
          <Block title="Decisions" count={snap.log.length} sub="Newest first, each with the balancer's reason."
            chips={[
              { label: 'Pods', on: logKinds.pods, onClick: () => setLogKinds({ ...logKinds, pods: !logKinds.pods }) },
              { label: 'Moves', on: logKinds.moves, onClick: () => setLogKinds({ ...logKinds, moves: !logKinds.moves }) },
              { label: 'Placements', on: logKinds.places, onClick: () => setLogKinds({ ...logKinds, places: !logKinds.places }) },
            ]}
            empty={log.length ? undefined : 'No decisions of these kinds yet.'}>
            {log.length > 0 && <div style={{ maxHeight: 420, overflow: 'auto' }}><Log lines={log.map((x) => ({ ts: clockAt(snap, x.at), t: x.why, tone: toneOf(x), hl: x.action.do === 'createPod' || x.action.do === 'resumePod' }))} /></div>}
          </Block>
        </Cell>
        <Cell span={mobile ? 12 : 5}>
          <Block title="Settings" sub={mode === 'mock' ? 'Apply now changes the running day; Restart starts a fresh one.' : 'Apply now changes the run from this moment.'}>
            <Settings cfg={cfg} set={setCfg} mock={mode === 'mock'} dirty={dirty} onApply={applyNow} onRestart={() => startMock(cfg)} />
          </Block>
        </Cell>
      </Grid>
    </>
  );
}

export function kindOf(d: Pick<Decision, 'action'>): 'pods' | 'moves' | 'places' {
  const a = d.action.do;
  if (a === 'move') return 'moves';
  if (a === 'pause') return 'places';
  if (a === 'placeOnFly' || a === 'placeOnPod' || a === 'wait' || a === 'reject') return a === 'reject' ? 'pods' : 'places';
  return 'pods';
}

export function toneOf(d: Pick<Decision, 'action' | 'why'>): Tone | undefined {
  const a = d.action;
  if (a.do === 'terminatePod' && /lost|broken|not ready|failed/.test(d.why)) return 'bad';
  if (a.do === 'reject') return 'bad';
  if (a.do === 'move' && a.emergency) return 'warn';
  if (a.do === 'pause') return 'warn';
  if (a.do === 'createPod' || a.do === 'resumePod' || a.do === 'undrain' || a.do === 'startSpeechMachine') return 'info';
  return undefined;
}

function clockParts(snap: Snapshot, t: number): { day: string; time: string } {
  if (snap.t0Unix !== null) {
    const d = new Date((snap.t0Unix + t) * 1000);
    return { day: d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }), time: `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}` };
  }
  const s = Math.max(0, Math.floor(t));
  return { day: `Day ${Math.floor(s / 86400) + 1}`, time: `${pad(Math.floor(s / 3600) % 24)}:${pad(Math.floor(s / 60) % 60)}:${pad(s % 60)}` };
}
function clockAt(snap: Snapshot, t: number): string { const c = clockParts(snap, t); return `${c.day} · ${c.time}`; }
const clock = (snap: Snapshot) => clockAt(snap, snap.t);

function elapsed(t: number): string {
  const s = Math.max(0, Math.floor(t));
  if (s < 3600) return `${Math.floor(s / 60)} min ${s % 60} s`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ${pad(Math.floor(s / 60) % 60)} min`;
  return `${Math.floor(s / 86400)} d ${Math.floor(s / 3600) % 24} h`;
}

function Clock({ snap }: { snap: Snapshot }) {
  const c = clockParts(snap, snap.t);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ display: 'grid', lineHeight: 1.15 }}>
        <span className="tbl__h">{c.day}</span>
        <span style={{ fontSize: 20, fontWeight: 700, fontVariantNumeric: 'tabular-nums', letterSpacing: '.01em' }}>{c.time}</span>
      </div>
      <div style={{ display: 'grid', lineHeight: 1.35, fontSize: 12, color: 'var(--ink-55)' }}>
        <span>{elapsed(snap.t)} in</span>
        <span>{snap.source}{snap.queued ? ` · ${snap.queued} to play` : ''}</span>
      </div>
    </div>
  );
}
